import { useState, type FormEvent, type JSX } from 'react';
import {
  useAccounts,
  useCreateAccount,
  useDeleteAccount,
  useStartLiveSession,
} from '../hooks/accounts';
import type { AccountMode, AccountSummary, TikTokCookie } from '../api/types';

interface AccountForm {
  mode: AccountMode;
  tiktokUsername: string;
  proxyUrl: string;
  tiktokEmail: string;
  tiktokPassword: string;
  cookiesJson: string;
}

const EMPTY_FORM: AccountForm = {
  mode: 'browserbase',
  tiktokUsername: '',
  proxyUrl: '',
  tiktokEmail: '',
  tiktokPassword: '',
  cookiesJson: '',
};

export function AccountsPanel(): JSX.Element {
  const { data: accounts, isLoading, error } = useAccounts();
  const createMutation = useCreateAccount();
  const deleteMutation = useDeleteAccount();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="dx-section-toolbar">
        <div className="dx-pane-meta">
          {accounts ? `${accounts.length} account${accounts.length === 1 ? '' : 's'}` : '—'}
        </div>
        <button className="dx-btn is-primary" onClick={() => setShowModal(true)}>
          + Add account
        </button>
      </div>

      {error && <div className="dx-error">{(error as Error).message}</div>}
      {isLoading && <div className="dx-loading">Loading accounts…</div>}

      {!isLoading && accounts && accounts.length === 0 && (
        <div className="dx-empty">
          <div className="dx-empty-title">No accounts yet</div>
          <div>Add a TikTok account to start running outreach campaigns.</div>
        </div>
      )}

      {!isLoading && accounts && accounts.length > 0 && (
        <table className="dx-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Mode</th>
              <th>Auth</th>
              <th>Status</th>
              <th>Added</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                onDelete={() => {
                  if (confirm(`Delete account "${a.label}"?`)) deleteMutation.mutate(a.id);
                }}
              />
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <AddAccountModal
          onClose={() => setShowModal(false)}
          onSubmit={(input) => {
            createMutation.mutate(input, { onSuccess: () => setShowModal(false) });
          }}
          submitting={createMutation.isPending}
          submitError={createMutation.error instanceof Error ? createMutation.error.message : null}
        />
      )}
    </>
  );
}

function AccountRow({
  account,
  onDelete,
}: {
  account: AccountSummary;
  onDelete: () => void;
}): JSX.Element {
  return (
    <tr>
      <td>
        <div className="dx-username">{account.label}</div>
        {account.tiktokUsername && account.tiktokUsername !== account.label && (
          <div className="dx-username-secondary">@{account.tiktokUsername}</div>
        )}
      </td>
      <td>
        <span className="dx-pill is-new" style={{ textTransform: 'capitalize' }}>
          {account.mode}
        </span>
      </td>
      <td style={{ fontSize: 11, color: 'var(--dx-text-2)' }}>
        {[account.hasCookies && 'Cookies', account.hasPassword && 'Password'].filter(Boolean).join(' + ') || '—'}
      </td>
      <td>
        <span className={`dx-pill is-${account.status === 'active' ? 'converted' : 'new'}`}>
          {account.status}
        </span>
      </td>
      <td className="dx-numeric" style={{ fontSize: 11 }}>
        {new Date(account.createdAt).toLocaleDateString()}
      </td>
      <td style={{ textAlign: 'right' }}>
        <button className="dx-btn is-ghost" onClick={onDelete}>
          Delete
        </button>
      </td>
    </tr>
  );
}

interface AddAccountModalProps {
  onClose: () => void;
  onSubmit: (input: {
    tiktokUsername?: string;
    proxyUrl?: string;
    tiktokEmail?: string;
    tiktokPassword?: string;
    cookies?: TikTokCookie[];
    mode: AccountMode;
  }) => void;
  submitting: boolean;
  submitError: string | null;
}

function AddAccountModal({ onClose, onSubmit, submitting, submitError }: AddAccountModalProps): JSX.Element {
  const [form, setForm] = useState<AccountForm>(EMPTY_FORM);
  const [parseError, setParseError] = useState<string | null>(null);
  const liveSession = useStartLiveSession();

  function update<K extends keyof AccountForm>(key: K, value: AccountForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    setParseError(null);

    let cookies: TikTokCookie[] | undefined;
    if (form.cookiesJson.trim()) {
      try {
        const parsed: unknown = JSON.parse(form.cookiesJson);
        if (!Array.isArray(parsed)) {
          setParseError('Cookies must be a JSON array');
          return;
        }
        cookies = parsed as TikTokCookie[];
      } catch {
        setParseError('Invalid cookie JSON — paste the array from your cookie-export extension');
        return;
      }
    }

    onSubmit({
      mode: form.mode,
      tiktokUsername: form.tiktokUsername.trim() || undefined,
      proxyUrl: form.proxyUrl.trim() || undefined,
      tiktokEmail: form.tiktokEmail.trim() || undefined,
      tiktokPassword: form.tiktokPassword.trim() || undefined,
      cookies,
    });
  }

  function openLiveSession() {
    liveSession.mutate(form.proxyUrl.trim() || undefined, {
      onSuccess: ({ liveViewUrl }) => {
        window.open(liveViewUrl, '_blank', 'noopener,noreferrer');
      },
    });
  }

  return (
    <div className="dx-modal-backdrop" onClick={onClose}>
      <div className="dx-modal" onClick={(ev) => ev.stopPropagation()}>
        <header className="dx-modal-header">
          <h3>Add TikTok account</h3>
          <button className="dx-btn is-ghost" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <form className="dx-modal-body" onSubmit={submit}>
          <div className="dx-form-row-stack">
            <label className="dx-modal-field">
              <span>Mode</span>
              <div className="dx-segmented">
                <button
                  type="button"
                  className={form.mode === 'browserbase' ? 'is-active' : ''}
                  onClick={() => update('mode', 'browserbase')}
                >
                  Browserbase (cloud)
                </button>
                <button
                  type="button"
                  className={form.mode === 'local' ? 'is-active' : ''}
                  onClick={() => update('mode', 'local')}
                >
                  Local (Mac)
                </button>
              </div>
              <small className="dx-hint">
                {form.mode === 'browserbase'
                  ? 'Cloud browser via Browserbase. Add a sticky residential proxy to hold a stable IP.'
                  : 'Use a local Playwright profile on this machine. Visible window for manual login.'}
              </small>
            </label>

            <label className="dx-modal-field">
              <span>TikTok username</span>
              <input
                className="dx-input"
                placeholder="yourhandle"
                value={form.tiktokUsername}
                onChange={(ev) => update('tiktokUsername', ev.target.value)}
              />
            </label>

            {form.mode === 'browserbase' && (
              <label className="dx-modal-field">
                <span>Proxy URL (optional)</span>
                <input
                  className="dx-input"
                  placeholder="http://user:pass@proxy.host:8000"
                  value={form.proxyUrl}
                  onChange={(ev) => update('proxyUrl', ev.target.value)}
                />
              </label>
            )}

            <label className="dx-modal-field">
              <span>Session cookies (JSON array)</span>
              <textarea
                className="dx-input"
                rows={6}
                placeholder='[{"name":"sessionid","value":"…","domain":".tiktok.com",...}]'
                value={form.cookiesJson}
                onChange={(ev) => update('cookiesJson', ev.target.value)}
                style={{ fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 11 }}
              />
              <small className="dx-hint">
                Export from a logged-in browser using a cookie-editor extension.
                {form.mode === 'browserbase' &&
                  ' Or use the live session below to log in manually inside a remote browser, then save.'}
              </small>
            </label>

            {form.mode === 'browserbase' && (
              <button
                type="button"
                className="dx-btn"
                onClick={openLiveSession}
                disabled={liveSession.isPending}
              >
                {liveSession.isPending ? 'Opening live session…' : 'Open live login session'}
              </button>
            )}

            <div className="dx-form-row" style={{ gap: 12 }}>
              <label className="dx-modal-field" style={{ flex: 1 }}>
                <span>Email (fallback)</span>
                <input
                  className="dx-input"
                  placeholder="login@example.com"
                  value={form.tiktokEmail}
                  onChange={(ev) => update('tiktokEmail', ev.target.value)}
                />
              </label>
              <label className="dx-modal-field" style={{ flex: 1 }}>
                <span>Password (fallback)</span>
                <input
                  className="dx-input"
                  type="password"
                  value={form.tiktokPassword}
                  onChange={(ev) => update('tiktokPassword', ev.target.value)}
                />
              </label>
            </div>
          </div>

          {(parseError || submitError) && (
            <div className="dx-error">{parseError ?? submitError}</div>
          )}

          <footer className="dx-modal-footer">
            <button type="button" className="dx-btn is-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="dx-btn is-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save account'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
